import React, { useState } from 'react';
import { ScheduleCalendar } from './ScheduleCalendar';
import { SalesForecastingDashboard } from './SalesForecastingDashboard';
import { JobManagement } from './JobManagement';
import { ShiftTemplates } from './ShiftTemplates';
import { EmployeeAvailability } from './EmployeeAvailability';
import { TimeOffRequests } from './TimeOffRequests';
import { ShiftOptimization } from './ShiftOptimization';
import './SchedulingDashboard.css';

interface SchedulingDashboardProps {
  storeId: string;
  enterpriseId: string;
}

type SchedulingView = 'overview' | 'calendar' | 'forecasting' | 'jobs' | 'templates' | 'availability' | 'timeoff' | 'optimization';

export const SchedulingDashboard: React.FC<SchedulingDashboardProps> = ({ 
  storeId, 
  enterpriseId 
}) => {
  const [activeView, setActiveView] = useState<SchedulingView>('overview');

  const schedulingTools = [
    {
      id: 'calendar' as const,
      title: 'Schedule Calendar',
      description: 'Create and manage employee schedules',
      shortcut: 'View weekly schedules and assign shifts'
    },
    {
      id: 'forecasting' as const,
      title: 'Sales Forecasting',
      description: 'AI-powered sales forecasting and shift optimization',
      shortcut: 'Predict sales and optimize staffing'
    },
    {
      id: 'jobs' as const,
      title: 'Job Management',
      description: 'Define job positions and pay rates',
      shortcut: 'Manage job definitions and requirements'
    },
    {
      id: 'templates' as const,
      title: 'Shift Templates',
      description: 'Create reusable shift templates',
      shortcut: 'Build standard shift patterns'
    },
    {
      id: 'availability' as const,
      title: 'Employee Availability',
      description: 'Manage employee availability and preferences',
      shortcut: 'Set and review availability windows'
    },
    {
      id: 'timeoff' as const,
      title: 'Time Off Requests',
      description: 'Review and approve time off requests',
      shortcut: 'Manage vacation and leave requests'
    },
    {
      id: 'optimization' as const,
      title: 'Shift Optimization',
      description: 'AI-powered shift optimization and recommendations',
      shortcut: 'Get intelligent staffing suggestions'
    }
  ];

  const renderActiveView = () => {
    switch (activeView) {
      case 'calendar':
        return <ScheduleCalendar storeId={storeId} />;
      case 'forecasting':
        return <SalesForecastingDashboard storeId={storeId} />;
      case 'jobs':
        return <JobManagement storeId={storeId} enterpriseId={enterpriseId} />;
      case 'templates':
        return <ShiftTemplates storeId={storeId} enterpriseId={enterpriseId} />;
      case 'availability':
        return <EmployeeAvailability storeId={storeId} enterpriseId={enterpriseId} />;
      case 'timeoff':
        return <TimeOffRequests storeId={storeId} enterpriseId={enterpriseId} />;
      case 'optimization':
        return <ShiftOptimization storeId={storeId} enterpriseId={enterpriseId} />;
      default:
        return (
          <div className="scheduling-overview">
            <div className="overview-header">
              <h2>Scheduling Tools</h2>
              <p>Choose a tool below to get started with employee scheduling and management.</p>
            </div>

            <div className="scheduling-grid">
              {schedulingTools.map((tool) => (
                <div
                  key={tool.id}
                  className="scheduling-card"
                  onClick={() => setActiveView(tool.id)}
                >
                  <div className="card-header">
                    <h3>{tool.title}</h3>
                  </div>
                  <div className="card-content">
                    <p className="description">{tool.description}</p>
                    <p className="shortcut">{tool.shortcut}</p>
                  </div>
                  <div className="card-action">
                    <span className="launch-text">Launch Tool →</span>
                  </div>
                </div>
              ))}
            </div>

            <div className="quick-actions">
              <h3>Quick Actions</h3>
              <div className="action-buttons">
                <button 
                  className="quick-btn primary"
                  onClick={() => setActiveView('calendar')}
                >
                  View This Week's Schedule
                </button>
                <button 
                  className="quick-btn secondary"
                  onClick={() => setActiveView('forecasting')}
                >
                  Generate Sales Forecast
                </button>
                <button 
                  className="quick-btn secondary"
                  onClick={() => setActiveView('optimization')}
                >
                  Optimize Shifts
                </button>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="scheduling-dashboard">
      {activeView !== 'overview' && (
        <div className="dashboard-header">
          <div className="breadcrumb">
            <button 
              className="breadcrumb-link"
              onClick={() => setActiveView('overview')}
            >
              Scheduling Tools
            </button>
            <span className="breadcrumb-separator">›</span>
            <span className="breadcrumb-current">
              {schedulingTools.find(tool => tool.id === activeView)?.title}
            </span>
          </div>
        </div>
      )}

      <div className="dashboard-content">
        {renderActiveView()}
      </div>
    </div>
  );
};